<template>
  <BaseCard :heading="$t('global.details')">
    <BaseGrid container>
      <BaseGrid
        v-if="customer.email"
        :size="12"
        v-test="'customer-email'"
      >
        <BaseText bold>{{ $t('global.email') }}</BaseText>
        <BaseText
          :href="`mailto:${customer.email}`"
          oneLine
        >
          {{ customer.email }}
        </BaseText>
      </BaseGrid>

      <BaseGrid
        v-if="customer.mobilePhone"
        :size="12"
        v-test="'customer-mobilePhone'"
      >
        <BaseText bold>{{ $t('global.mobile') }}</BaseText>
        <BaseText :href="`tel:${customer.mobilePhone}`">
          {{ customer.mobilePhone }}
        </BaseText>
      </BaseGrid>

      <BaseGrid
        v-if="customer.phone"
        :size="12"
        v-test="'customer-phone'"
      >
        <BaseText bold>{{ $t('global.phone') }}</BaseText>
        <BaseText :href="`tel:${customer.phone}`">
          {{ customer.phone }}
        </BaseText>
      </BaseGrid>

      <BaseGrid
        v-if="customer.address"
        :size="12"
        v-test="'customer-address'"
      >
        <BaseText bold>{{ $t('global.address') }}</BaseText>
        <BaseText>{{ customer.address }}</BaseText>
        <BaseText v-if="customer.zipcode && customer.city">
          {{ customer.zipcode }}
          {{ customer.city }}
        </BaseText>
        <BaseText
          size="s"
          :href="`http://maps.google.com/?q=${customer.address}`"
        >
          {{ $t('global.actions.view_on_map') }}
        </BaseText>
      </BaseGrid>

      <BaseGrid
        v-if="customer.gender"
        :size="12"
        v-test="'customer-gender'"
      >
        <BaseText bold>{{ $t('global.gender') }}</BaseText>
        <BaseText>
          <template v-if="customer.gender === 'MALE'">
            {{ $t('global.male') }}
          </template>
          <template v-else-if="customer.gender === 'FEMALE'">
            {{ $t('global.female') }}
          </template>
          <template v-else-if="customer.gender === 'NONBINARY'">
            {{ $t('global.nonbinary') }}
          </template>
          <template v-else>{{ $t('global.unknown') }}</template>
        </BaseText>
      </BaseGrid>

      <BaseGrid
        v-if="customer.dateOfBirth"
        :size="12"
        v-test="'customer-dateOfBirth'"
      >
        <BaseText bold>{{ $t('global.date_of_birth') }}</BaseText>
        <Flex :gap=".25">
          <BaseText>{{ filters.date(customer.dateOfBirth) }}</BaseText>
          <BaseText color="secondary">
            ({{ age }}
            {{ $t('customers.years') }})
          </BaseText>
          <BaseIcon
            v-if="isBirthday"
            :tooltip="$t('customers.birthday_today')"
            name="gift"
            color="success"
          />
        </Flex>
      </BaseGrid>

      <BaseGrid
        :size="12"
        v-test="'customer-createdAt'"
      >
        <BaseText bold>{{ $t('global.created') }}</BaseText>
        <BaseText>{{ filters.date(customer.createdAt) }}</BaseText>
      </BaseGrid>

      <BaseGrid
        :size="12"
        v-test="'customer-id'"
      >
        <BaseText bold>{{ $t('global.salonized_id', brandName()) }}</BaseText>
        <BaseText>{{ customer.id }}</BaseText>
      </BaseGrid>

      <BaseGrid
        v-for="(field, index) in customFields"
        :key="index"
        :size="12"
        v-test="'customer-customFields'"
      >
        <BaseText bold>{{ field.label }}</BaseText>
        <BaseText>{{ field.value }}</BaseText>
      </BaseGrid>

      <BaseGrid
        v-if="customer.customerNotes"
        :size="12"
        v-test="'customer-customerNotes'"
      >
        <BaseText bold>{{ $t('customers.additional_customer_info') }}</BaseText>
        <BaseText>{{ customer.customerNotes }}</BaseText>
      </BaseGrid>

      <BaseGrid
        v-if="customer.alertNotes"
        :size="12"
        v-test="'customer-alertNotes'"
      >
        <BaseText bold>
          {{ $t('customers.warning_saving_appointments') }}
        </BaseText>
        <BaseText>{{ customer.alertNotes }}</BaseText>
      </BaseGrid>
    </BaseGrid>
  </BaseCard>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import dayjs from '@/dayjs';
import filters from '@/filters';
import { brandName } from '@/helpers/brand-name';
import { useCompanyStore } from '@/stores/company';

export default defineComponent({
  props: {
    customer: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return {
      filters,
      brandName,
    };
  },
  computed: {
    age() {
      const dateOfBirth = this.customer.dateOfBirth;
      return dateOfBirth
        ? Math.trunc(dayjs().diff(dateOfBirth, 'y', true))
        : null;
    },
    isBirthday() {
      const birthday = dayjs(this.customer.dateOfBirth).format('DDMM');
      const now = dayjs().format('DDMM');
      return birthday === now;
    },
    customFields() {
      const { companySettings } = useCompanyStore();
      const fields = [];
      for (let i = 0; i < 10; i++) {
        const label = companySettings.customers[`custom${i + 1}Label`];
        const value = this.customer[`custom${i + 1}`];

        if (label && value) {
          fields.push({
            label,
            value,
          });
        }
      }
      return fields;
    },
  },
});
</script>
